import { useState, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { useStore } from '@tanstack/react-store'
import { create } from '@bufbuild/protobuf'
import { Plus } from 'lucide-react'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { getSystemSettingsQueryOptions, useSystemSettings } from '@/stores/settings.store'
import { organizationStore } from '@/stores/organization.store'
import { listTasksQueryOptions, useTasksList, useUpdateTask, usePrefetchTask } from '@/stores/tasks.store'
import {
  tasksFiltersStore,
  useTasksSelectedStatuses,
  useTasksDueDateRange,
} from '@/stores/tasks-filters.store'
import { TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getTaskStatusColor } from '@/lib/enum-utils'
import { TasksKanban, badgeVariantToTailwind } from '@/components/tasks/tasks-kanban'
import { TaskSheet } from '@/components/tasks/task-sheet'
import { mapTaskLiteToKanbanItem } from '@/components/tasks/types'
import { getTask } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetTaskRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { Button } from '@/components/ui/button'
import type { Task } from '@/gen/cortado/v1/actions_pb'
import { canPreloadOrganizationQueries, preloadQueries } from '@/lib/route-preloading'

export const Route = createFileRoute('/_app/_home/tasks')({
  component: TasksPage,
  loader: ({ context }) => {
    if (!canPreloadOrganizationQueries(context.auth)) return
    const organizationId = organizationStore.state.selectedOrganizationId
    if (!organizationId) return

    const filters = tasksFiltersStore.state
    preloadQueries(context.queryClient, [
      getSystemSettingsQueryOptions(organizationId),
      listTasksQueryOptions({
        organizationId,
        statuses: filters.selectedStatuses.length > 0 ? filters.selectedStatuses : undefined,
        dueDateStart: filters.dueDateRange?.from,
        dueDateEnd: filters.dueDateRange?.to,
      }),
    ])
  },
})

function TasksPage() {
  useBreadcrumbFromRoute()
  const { settings, isLoading: isSettingsLoading } = useSystemSettings()
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const [createSheetOpen, setCreateSheetOpen] = useState(false)

  // Filter state from store - used for backend filtering
  const selectedStatuses = useTasksSelectedStatuses()
  const dueDateRange = useTasksDueDateRange()

  // Fetch tasks with backend filters
  const { tasks, isLoading: isTasksLoading, hasLoaded } = useTasksList({
    statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    dueDateStart: dueDateRange?.from,
    dueDateEnd: dueDateRange?.to,
  })

  // Map to kanban items
  const kanbanItems = useMemo(
    () => tasks.map(mapTaskLiteToKanbanItem),
    [tasks]
  )

  // Status change handler
  const { updateTaskStatus } = useUpdateTask()
  const handleTaskMove = async (
    itemId: string,
    newStatus: TaskStatusEnum,
    _oldStatus: TaskStatusEnum
  ) => {
    await updateTaskStatus(itemId, newStatus)
  }

  // Prefetch on hover
  const { prefetchTask } = usePrefetchTask()
  const handleCardHover = (taskId: string) => {
    prefetchTask(taskId)
  }

  // Edit task state
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Fetch full task data when editing
  const taskQuery = useQuery(
    getTask,
    create(GetTaskRequestSchema, { id: selectedTaskId || '' }),
    { enabled: !!selectedTaskId && sheetOpen }
  )
  const selectedTask = taskQuery.data?.task as Task | undefined

  const handleCardClick = (taskId: string) => {
    setSelectedTaskId(taskId)
    setSheetOpen(true)
  }

  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open)
    if (!open) {
      setSelectedTaskId(null)
    }
  }

  // Build columns from settings (excluding UNKNOWN)
  const taskColumns = useMemo(() => {
    return (settings?.taskStatuses || [])
      .filter((status) => status !== TaskStatusEnum.TASK_STATUS_UNKNOWN)
      .map((status) => ({
        status,
        label: formatEnumName(status, TaskStatusEnum),
        color: badgeVariantToTailwind(getTaskStatusColor(status)),
      }))
  }, [settings?.taskStatuses])

  // Show loading state while settings or initial tasks load
  const isLoading = isSettingsLoading || isTasksLoading || !hasLoaded

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <TasksKanban
        data={kanbanItems}
        columns={taskColumns}
        onItemMove={handleTaskMove}
        onCardClick={handleCardClick}
        onCardHover={handleCardHover}
        isLoading={isLoading}
        toolbarRight={
          <Button onClick={() => setCreateSheetOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        }
      />

      {/* Create Task Sheet */}
      {selectedOrganizationId && (
        <TaskSheet
          organizationId={selectedOrganizationId}
          open={createSheetOpen}
          onOpenChange={setCreateSheetOpen}
        />
      )}

      {/* Edit Task Sheet */}
      {selectedTask && (
        <TaskSheet
          organizationId={selectedTask.organizationId}
          parentType={selectedTask.parentType}
          parentId={selectedTask.parentId}
          task={selectedTask}
          open={sheetOpen}
          onOpenChange={handleSheetOpenChange}
        />
      )}
    </div>
  )
}
